import React, { useState, useEffect, useMemo } from "react";
import "./LudoChampionsLeague.css";

const ROUNDS = ["Tour 1", "Quart", "Demi", "Finale"];
const CURRENT_ROUND = 0; // index dans ROUNDS
const PRIZE_START = 1200;
const PRIZE_TARGET = 6000;

// Ancres de défilement (une seule page)
const SECTIONS = [
  { key: "duel", label: "Duel", icon: "⚔" },
  { key: "infos", label: "Infos", icon: "◈" },
  { key: "live", label: "Live", icon: "▶" },
  { key: "classement", label: "Classement", icon: "#" },
  { key: "historique", label: "Historique", icon: "≡" },
];

// Joueuses du duel : p1 = bleu, p2 = or
const PLAYERS = {
  p1: { name: "Sirine Elghali", first: "Sirine", initials: "SE", seed: 3, status: "Prête", wins: 35, points: 2650 },
  p2: { name: "Manel Lejmi", first: "Manel", initials: "ML", seed: 1, status: "En attente", wins: 42, points: 2850 },
};

const rankingData = [
  { rank: 1, name: "Manel Lejmi", country: "Tunisie", points: 2850, wins: 42 },
  { rank: 2, name: "Ahmed Ben Ali", country: "Tunisie", points: 2720, wins: 38 },
  { rank: 3, name: "Sirine Elghali", country: "Tunisie", points: 2650, wins: 35 },
  { rank: 4, name: "Fatima Zahra", country: "Tunisie", points: 2500, wins: 30 },
  { rank: 5, name: "Mohamed Salah", country: "Tunisie", points: 2400, wins: 28 },
];

const pastMatches = [
  { winner: "Sirine Elghali", loser: "Ahmed Ben Ali", score: "2-1", date: "3 janv. 2026" },
  { winner: "Manel Lejmi", loser: "Fatima Zahra", score: "2-0", date: "4 janv. 2026" },
  { winner: "Sirine Elghali", loser: "Mohamed Salah", score: "2-1", date: "5 janv. 2026" },
];

const upcomingMatches = [
  { player1: "Manel Lejmi", player2: "Gagnant·e tour 1", info: "Tour 2 · BO3", date: "Demain · 20h00" },
  { player1: "À déterminer", player2: "À déterminer", info: "Quart de finale 1", date: "10 janv. 2026 · 19h00" },
  { player1: "À déterminer", player2: "À déterminer", info: "Quart de finale 2", date: "12 janv. 2026 · 20h30" },
];

const Side = ({ id, player }) => (
  <div className={`lcl-side lcl-side--${id}`}>
    <span className="lcl-side__ghost" aria-hidden="true">{player.initials}</span>
    <span className="lcl-side__seed">SEED {player.seed} · {player.status.toUpperCase()}</span>
    <h2 className="lcl-side__name">{player.name}</h2>
    <span className="lcl-side__stats">{player.wins} victoires · {player.points.toLocaleString("fr-FR")} pts</span>
  </div>
);

const LudoChampionsLeague = ({
  dashboardUrl = "/dashboard",
  liveUrl = "https://www.youtube.com/example-live-stream",
}) => {
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [prize, setPrize] = useState(PRIZE_START);
  const [activeSection, setActiveSection] = useState("duel");
  const [votes, setVotes] = useState({ p1: 58, p2: 42 });
  const [userVote, setUserVote] = useState(null);

  // Animation de la cagnotte
  useEffect(() => {
    const step = Math.ceil((PRIZE_TARGET - PRIZE_START) / 60);
    const interval = setInterval(() => {
      setPrize((prev) => {
        const next = prev + step;
        if (next >= PRIZE_TARGET) {
          clearInterval(interval);
          return PRIZE_TARGET;
        }
        return next;
      });
    }, 30);
    return () => clearInterval(interval);
  }, []);

  // Scrollspy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveSection(e.target.id)),
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (key) => {
    const el = document.getElementById(key);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const prizePct = useMemo(
    () => Math.min(100, Math.round(((prize - PRIZE_START) / (PRIZE_TARGET - PRIZE_START)) * 100)),
    [prize]
  );

  const totalVotes = votes.p1 + votes.p2;
  const p1Pct = Math.round((votes.p1 / totalVotes) * 100);
  const p2Pct = 100 - p1Pct;

  const handleVote = (id) => {
    if (userVote) return;
    setVotes((prev) => ({ ...prev, [id]: prev[id] + 1 }));
    setUserVote(id);
  };

  return (
    <div className="lcl">
      {/* ===== TOP BAR ===== */}
      <header className="lcl-top">
        <a href={dashboardUrl} className="lcl-logo" aria-label="Retour au dashboard">
          <span className="lcl-logo__tag">LCL</span>
          <span className="lcl-logo__text">CHAMPIONS LEAGUE</span>
        </a>
        <nav className="lcl-nav" aria-label="Sections">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`lcl-nav__item ${activeSection === s.key ? "is-active" : ""}`}
              onClick={() => scrollToSection(s.key)}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <span className="lcl-live"><i />EN DIRECT</span>
        <button type="button" className="lcl-btn lcl-btn--top" onClick={() => setShowJoinModal(true)}>
          REJOINDRE
        </button>
      </header>

      <main className="lcl-main">
        {/* ===== DUEL ===== */}
        <section id="duel" className="lcl-section lcl-section--hero">
          <div className="lcl-arena">
            <Side id="p1" player={PLAYERS.p1} />

            <div className="lcl-center">
              <div className="lcl-vs">VS</div>
              <div className="lcl-engage">
                <span className="lcl-round">{ROUNDS[CURRENT_ROUND].toUpperCase()} · BO3</span>
                <span className="lcl-countdown">Prochaine partie <strong>12:34</strong></span>

                <div className="lcl-predict">
                  <div className="lcl-predict__btns">
                    {["p1", "p2"].map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={`lcl-predict__btn lcl-predict__btn--${id} ${userVote === id ? "is-chosen" : ""}`}
                        onClick={() => handleVote(id)}
                        disabled={!!userVote}
                      >
                        {PLAYERS[id].first}
                      </button>
                    ))}
                  </div>
                  <div className="lcl-predict__track">
                    <div className="lcl-predict__fill lcl-predict__fill--p1" style={{ width: `${p1Pct}%` }} />
                    <div className="lcl-predict__fill lcl-predict__fill--p2" style={{ width: `${p2Pct}%` }} />
                  </div>
                  <div className="lcl-predict__labels">
                    <span className="is-p1">{p1Pct}% {PLAYERS.p1.first}</span>
                    <span className="is-p2">{PLAYERS.p2.first} {p2Pct}%</span>
                  </div>
                  <span className="lcl-predict__note">
                    {userVote ? "Merci pour ton pronostic !" : `${totalVotes} pronostics`}
                  </span>
                </div>
              </div>
            </div>

            <Side id="p2" player={PLAYERS.p2} />
          </div>
        </section>

        {/* ===== INFOS ===== */}
        <section id="infos" className="lcl-section">
          <h2 className="lcl-h2">Infos du tournoi</h2>
          <div className="lcl-cards">
            <div className="lcl-card">
              <span className="lcl-label">PARCOURS</span>
              <ol className="lcl-steps">
                {ROUNDS.map((r, i) => (
                  <li
                    key={r}
                    className={i === CURRENT_ROUND ? "is-current" : i < CURRENT_ROUND ? "is-done" : ""}
                  >
                    <span className="lcl-steps__dot">{i < CURRENT_ROUND ? "✓" : i + 1}</span>
                    {r}
                  </li>
                ))}
              </ol>
            </div>

            <div className="lcl-card">
              <span className="lcl-label">CAGNOTTE</span>
              <span className="lcl-big">{prize.toLocaleString("fr-FR")} <em>USD</em></span>
              <div className="lcl-bar"><div style={{ width: `${prizePct}%` }} /></div>
              <span className="lcl-sub">Objectif {PRIZE_TARGET.toLocaleString("fr-FR")} USD</span>
            </div>

            <div className="lcl-card">
              <span className="lcl-label">FORMAT</span>
              <span className="lcl-big">BO3</span>
              <span className="lcl-sub">4 tours · élimination directe</span>
            </div>

            <div className="lcl-card">
              <span className="lcl-label">PARTICIPANTS</span>
              <span className="lcl-big">16</span>
              <span className="lcl-sub">Ouvert débutant·e à confirmé·e</span>
            </div>
          </div>
          <div className="lcl-cta">
            <button type="button" className="lcl-btn" onClick={() => setShowJoinModal(true)}>
              REJOINDRE LE TOURNOI
            </button>
          </div>
        </section>

        {/* ===== LIVE ===== */}
        <section id="live" className="lcl-section">
          <h2 className="lcl-h2">En direct</h2>
          <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="lcl-livecard">
            <span className="lcl-livecard__badge"><i />DIRECT</span>
            <span className="lcl-livecard__title">{PLAYERS.p1.first} vs {PLAYERS.p2.first}</span>
            <span className="lcl-livecard__sub">{ROUNDS[CURRENT_ROUND]} · BO3 · Regarder maintenant</span>
            <span className="lcl-livecard__play" aria-hidden="true">▶</span>
          </a>
        </section>

        {/* ===== CLASSEMENT ===== */}
        <section id="classement" className="lcl-section">
          <h2 className="lcl-h2">Classement</h2>
          <div className="lcl-tablewrap">
            <table className="lcl-table">
              <thead>
                <tr>
                  <th>Rang</th><th>Joueur</th><th className="lcl-hide-sm">Pays</th>
                  <th className="num">Points</th><th className="num">Victoires</th>
                </tr>
              </thead>
              <tbody>
                {rankingData.map((p) => (
                  <tr key={p.rank} className={p.rank <= 3 ? `podium-${p.rank}` : ""}>
                    <td className="rank">{p.rank}</td>
                    <td>{p.name}</td>
                    <td className="muted lcl-hide-sm">{p.country}</td>
                    <td className="num">{p.points.toLocaleString("fr-FR")}</td>
                    <td className="num">{p.wins}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ===== HISTORIQUE ===== */}
        <section id="historique" className="lcl-section">
          <h2 className="lcl-h2">Historique</h2>
          <div className="lcl-history">
            <div>
              <h3 className="lcl-label">PARTIES JOUÉES</h3>
              <ul className="lcl-list">
                {pastMatches.map((m, i) => (
                  <li key={i}>
                    <div className="lcl-list__row">
                      <span><strong>{m.winner}</strong> bat {m.loser}</span>
                      <span className="muted">{m.score}</span>
                    </div>
                    <span className="lcl-list__date">{m.date}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="lcl-label">À VENIR</h3>
              <ul className="lcl-list">
                {upcomingMatches.map((m, i) => (
                  <li key={i}>
                    <div className="lcl-list__row">
                      <span>{m.player1} vs {m.player2}</span>
                      <span className="muted">{m.info}</span>
                    </div>
                    <span className="lcl-list__date is-accent">{m.date}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* ===== BOTTOM NAV (mobile) ===== */}
      <nav className="lcl-bottom" aria-label="Sections">
        <a href={dashboardUrl} className="lcl-bottom__item">
          <span className="lcl-bottom__icon">←</span>Dashboard
        </a>
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`lcl-bottom__item ${activeSection === s.key ? "is-active" : ""}`}
            onClick={() => scrollToSection(s.key)}
          >
            <span className="lcl-bottom__icon">{s.icon}</span>{s.label}
          </button>
        ))}
      </nav>

      {/* ===== MODAL ===== */}
      {showJoinModal && (
        <div className="lcl-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="lcl-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h2>Rejoindre la LCL 2026</h2>
            <p>
              Inscris-toi pour participer au tournoi. On te recontacte avec les détails
              d'inscription, les règles et les conditions de qualification.
            </p>
            <p className="lcl-modal__hi">Cagnotte jusqu'à 6 000 USD · ouvert à toutes et tous.</p>
            <form className="lcl-form" onSubmit={(e) => e.preventDefault()}>
              <input type="text" placeholder="Ton nom" required />
              <input type="email" placeholder="Ton email" required />
              <input type="tel" placeholder="Ton numéro de téléphone" />
              <textarea placeholder="Niveau de jeu, expérience..." rows="4" />
              <button type="submit" className="lcl-btn">ENVOYER MA CANDIDATURE</button>
            </form>
            <button type="button" className="lcl-modal__close" onClick={() => setShowJoinModal(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LudoChampionsLeague;